/**
 * Identity between two SHORT LABELS naming one company.
 *
 * MUST stay in lock-step with `scoring.same_entity` / `scoring.host_is_entity` in
 * the Brand Perception agent, which computes the persisted competitor matrix and
 * suggestion list this UI renders. A one-sided change makes the frontend disagree
 * with the stored data it is displaying.
 *
 * The question is NOT "does this name appear in this text" (that is
 * `is_brand_cited`, agent-side only, word-boundary over a full answer). Both
 * sides here are labels, each spelling the company its own way, so containment
 * has to work in both directions:
 *
 *   "Caroline Tissier"                    ↔ "Caroline Tissier Intérieurs"
 *   "Caroline Tissier - Architecte d'…"   ↔ "Caroline Tissier Intérieurs"
 *   "NATHALIE NIVET INTÉRIEUR DESIGN"     ↔ "Nathalienivet design"
 *
 * All three were treated as different companies until 2026-09-30 — the first two
 * cost the brand its recommendation rank on every engine but Perplexity, the
 * third made a tracked competitor read `absent` on prompts that ranked it #2 and
 * offered it back as a new competitor to track.
 */

/** Separators that start a DESCRIPTOR rather than continue the name. */
const DESCRIPTOR_SPLIT = /\s[-–—|:]\s|\s*[([,;]/;

/** Below this many slug chars, containment is meaningless ("Studio", "Design"). */
const MIN_SLUG = 8;

/** Significant accent-folded alphanumeric tokens; ≤2 chars carry no identity. */
export function entityNameTokens(name: string | undefined | null): string[] {
  const folded = (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return folded.split(/[^a-z0-9]+/).filter((t) => t.length > 2);
}

/** Every token appears in `slug`, in order, without overlapping. */
function tokensInOrder(tokens: string[], slug: string): boolean {
  let pos = 0;
  for (const t of tokens) {
    const at = slug.indexOf(t, pos);
    if (at < 0) return false;
    pos = at + t.length;
  }
  return true;
}

/** The label itself, plus its head before a descriptor separator. */
function forms(label: string | undefined | null): string[] {
  const l = (label || '').trim();
  if (!l) return [];
  const head = l.split(DESCRIPTOR_SPLIT)[0].trim();
  return !head || head === l ? [l] : [l, head];
}

function sameForm(a: string, b: string): boolean {
  const ta = entityNameTokens(a);
  const tb = entityNameTokens(b);
  if (ta.length === 0 || tb.length === 0) return false;
  const sa = ta.join('');
  const sb = tb.join('');
  if (sa === sb) return true;
  if (sa.length >= MIN_SLUG && tokensInOrder(ta, sb)) return true;
  return sb.length >= MIN_SLUG && tokensInOrder(tb, sa);
}

/** True iff two short labels name the same company. Symmetric. */
export function sameEntityName(
  a: string | undefined | null,
  b: string | undefined | null,
): boolean {
  for (const left of forms(a)) {
    for (const right of forms(b)) {
      if (sameForm(left, right)) return true;
    }
  }
  return false;
}

/**
 * True iff `host` looks like `name`'s OWN site — compared against the dotless
 * host ("Deux.io" ↔ deux.io) and the part before the public suffix
 * ("NATHALIE NIVET INTÉRIEUR DESIGN" ↔ nathalienivet-design.com).
 */
export function hostMatchesEntityName(
  host: string | undefined | null,
  name: string | undefined | null,
): boolean {
  const h = (host || '').trim().toLowerCase().replace(/^\.+|\.+$/g, '');
  if (!h || !h.includes('.')) return false;
  const labels = h.split('.');
  return (
    sameEntityName(labels.join(''), name) || sameEntityName(labels.slice(0, -1).join('.'), name)
  );
}
